import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { User } from '@client/components/_ui/display/User';
import { ClaimClientUpdateIndicator } from '@client/components/claims/ClaimClientUpdateIndicator';
import {
    type ClaimReassignmentAction,
    ClaimReassignmentDrawer,
} from '@client/components/claims/ClaimReassignmentDrawer';
import { ClaimStatusTag } from '@client/components/claims/ClaimStatusTag';
import { ContactPopover } from '@client/components/contacts/ContactPopover';
import { buildInitialWhereFromFilters, FilterBar } from '@client/components/filters';
import { isWhereClauseEmpty } from '@client/components/filters/core/where-builder';
import { TableViewsBar, useEntityBuiltInViews, useTableViews } from '@client/components/table-views';
import { ICONS } from '@client/constants/icons';
import { useConfigServiceLoader } from '@client/hooks/business/configuration/useConfigServiceLoader';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, TableEmptyState, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { selectOrg, selectUser, useAuthStore } from '@client/stores/AuthStore';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import {
    CONFIG_LOOKUP_QUERY_OPTIONS,
    LIST_ROUTE_PREFETCH_OPTIONS,
    LIST_ROUTE_QUERY_OPTIONS,
} from '@client/trpc/query-options';
import type { ShareableRouteContext } from '@client/types/router';
import { TokenRefreshManager } from '@client/utils/auth/TokenRefreshManager';
import { useContentReadyMark } from '@client/utils/performance/content-ready';
import { DEFAULT_MAX_ACCESSIBLE_RECORDS, DEFAULT_PAGE_SIZE, ORG_ROLES } from '@shared/constants';
import { FilterOperatorSchema } from '@shared/schemas/filter';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Tag } from 'antd';
import type { Key } from 'antd/es/table/interface';
import { useState } from 'react';
import { z } from 'zod';
import { runDetailRoutePrefetchWhenAuthorised, shouldPrefetchDetailRouteData } from '../detail-route-prefetch';
import { freshClaimsListInput, freshClaimsListInputWithWhere, LIST_TABLE_PERSIST_KEYS } from '../fresh-list-inputs';
import { runListRoutePrefetch } from '../list-route-prefetch';
import { useClaimFilterConfig } from './useClaimFilterConfig';

export const Route = createFileRoute('/console/$orgId/claims/')({
    staticData: {
        ui: {
            title: 'View claims',
            subtitle: 'Search and manage claims',
            icon: ICONS.claim,
        },
    },
    validateSearch: zodValidator(
        z.object({
            'claim-filters': z
                .array(
                    z.union([
                        z.object({
                            id: z.string(),
                            filterId: z.literal('owner-ids'),
                            operator: FilterOperatorSchema,
                            value: z.union([z.array(z.coerce.bigint()), z.coerce.bigint()]),
                        }),
                        z.object({
                            id: z.string(),
                            filterId: z.string(),
                            operator: FilterOperatorSchema,
                            value: z.any(),
                        }),
                    ]),
                )
                .optional()
                .catch(undefined),
            view: z.string().optional().catch(undefined),
        }),
    ),
    beforeLoad: ({ context, params, search, location, preload, abortController }): ShareableRouteContext => {
        // Start the saved-views round trip (and, when the initial input is exactly
        // computable, the list query itself) while the auth gate is still rendering.
        // Without this the chain is strictly serial after mount:
        // listSavedViews → viewsReady → listClaims (CUR-1036, task 2.2). Deferred
        // until the session restores on cold deep links, same as the detail routes.
        runDetailRoutePrefetchWhenAuthorised(
            () => {
                const authState = useAuthStore.getState();
                const user = selectUser(authState);
                return shouldPrefetchDetailRouteData({
                    hasValidToken: authState.hasValidToken({
                        minValidityMs: TokenRefreshManager.REFRESH_BUFFER_MS,
                    }),
                    userRoles: user?.roles,
                    allowedRoles: [ORG_ROLES.USER, ORG_ROLES.SHARED_EDIT_USER, ORG_ROLES.ORG_ADMIN],
                    hydratedOrg: selectOrg(authState),
                    routeOrgId: params.orgId,
                    requiredModule: 'claim',
                });
            },
            () => {
                runListRoutePrefetch<RouterInputs['claim']['listClaims'], 'CLAIM'>({
                    savedViewsUtils: context.trpcUtils.savedView.listSavedViews,
                    entityType: 'CLAIM',
                    pathname: location.pathname,
                    urlFilterCount: search['claim-filters']?.length ?? 0,
                    urlViewId: search.view,
                    defaultInput: freshClaimsListInput(),
                    prefetchList: (input) => {
                        void context.trpcUtils.claim.listClaims.prefetch(input, LIST_ROUTE_PREFETCH_OPTIONS);
                    },
                });
                // The client-update column config gates the table's loading overlay —
                // warm it alongside the list so neither serialises behind the other.
                void context.trpcUtils.configurationItem.getConfigurationItem.prefetch(
                    { name: 'CLAIM_CLIENT_UPDATE_CONFIGURATION' },
                    { ...CONFIG_LOOKUP_QUERY_OPTIONS, meta: { errorSurface: 'silent', captureToSentry: false } },
                );
            },
            { defer: !preload, signal: abortController.signal },
        );
        return undefined;
    },
    component: ClaimSearch,
});

type QueryParams = RouterInputs['claim']['listClaims'];

const columnHelper = createColumnHelper<RouterOutputs['claim']['listClaims']['rows'][number], QueryParams['orderBy']>();

function ClaimSearch() {
    const orgId = useOrgId();
    const searchParams = Route.useSearch();
    const claimClientUpdateConfigurationQuery = useConfigServiceLoader(async (configService) =>
        configService.fetchClaimClientUpdateConfiguration({
            // Shares the freshness window with the `beforeLoad` prefetch so the warm
            // cache is honoured instead of refetching on every mount (CUR-1036).
            staleTime: CONFIG_LOOKUP_QUERY_OPTIONS.staleTime,
        }),
    );
    const { formatDate } = useFormatter();
    const navigate = useNavigate();

    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const [reassignmentAction, setReassignmentAction] = usePathState<ClaimReassignmentAction>('reassignment-action', [
        'none',
    ]);
    const { config: filterConfig, ready: filterConfigReady } = useClaimFilterConfig();

    const [queryParams, setQueryParams] = usePathState<QueryParams>(
        'query-params',
        freshClaimsListInputWithWhere(buildInitialWhereFromFilters(filterConfig, searchParams['claim-filters'])),
    );

    // Postpone the list query until saved views resolve which view loads first, so we fetch once
    // with the resolved where/sort/pageSize instead of unfiltered-then-refiltered (set by onResolved).
    const [viewsReady, setViewsReady] = useState(false);

    const listClaimsQuery = trpc.claim.listClaims.useQuery(queryParams, {
        enabled: filterConfigReady && viewsReady,
        ...LIST_ROUTE_QUERY_OPTIONS,
    });

    // LCP guardrail (CUR-1036, task 0.1): mark when real rows (or the genuine empty
    // state) commit — static chrome painting earlier must not move this.
    useContentReadyMark(listClaimsQuery.isSuccess);

    const tableProps = usePrismaTable({
        data: listClaimsQuery.data?.rows || [],
        total: listClaimsQuery.data?.total || 0,
        loading: !viewsReady || listClaimsQuery.isLoading || claimClientUpdateConfigurationQuery.isLoading,
        columns: [
            columnHelper.accessor('generatedId', {
                id: 'claimNo',
                header: 'Claim#',
                sortable: 'generatedId',
                cell({ row }) {
                    return row.generatedId || row.id;
                },
                fixed: 'left',
                hideable: false,
                className: 'font-bold whitespace-nowrap',
            }),
            columnHelper.display({
                id: 'clientUpdate',
                header: claimClientUpdateConfigurationQuery.data?.headerWidgetLabel || 'Client update',
                cell({ row }) {
                    return (
                        <ClaimClientUpdateIndicator
                            variant="compact"
                            summary={row.clientUpdateSummary}
                            data={{
                                claimId: row.id,
                                Status: row.Status,
                            }}
                        />
                    );
                },
                hideable: false,
                className: claimClientUpdateConfigurationQuery.data?.isEnabled ? undefined : 'hidden',
            }),
            columnHelper.accessor((row) => row.Owner?.userId, {
                id: 'owner',
                header: 'Owner',
                sortable: 'Owner.firstName',
                cell({ row }) {
                    return (
                        <User userId={row.Owner?.userId} name={row.Owner?.fullName} photoUrl={row.Owner?.photoUrl} />
                    );
                },
            }),
            columnHelper.accessor('Status', {
                id: 'status',
                header: 'Status',
                sortable: 'Status.name',
                cell({ row }) {
                    return <ClaimStatusTag data={row.Status} />;
                },
                className: 'whitespace-nowrap',
            }),
            columnHelper.accessor('policyNo', {
                id: 'policyNo',
                header: 'Policy#',
                sortable: 'policyNo',
                className: 'font-bold',
            }),
            columnHelper.accessor('decision', {
                id: 'decision',
                header: 'Decision',
                sortable: 'decision',
                cell({ row }) {
                    return <Tag variant="outlined">{row.decision}</Tag>;
                },
            }),
            columnHelper.accessor((row) => row.PrimaryPolicyHolderContact?.fullName, {
                id: 'fullName',
                header: 'Policy holder',
                cell({ row }) {
                    return (
                        <ContactPopover id={row.PrimaryPolicyHolderContact.id}>
                            {row.PrimaryPolicyHolderContact?.fullName}
                        </ContactPopover>
                    );
                },
            }),
            columnHelper.accessor('causeOfLoss', {
                id: 'causeOfLoss',
                header: 'Cause of loss',
                sortable: 'causeOfLoss',
            }),
            columnHelper.accessor('claimOccurredAt', {
                id: 'dateOfLoss',
                header: 'Date of loss',
                sortable: 'claimOccurredAt',
                cell({ row }) {
                    return formatDate(row.claimOccurredAt);
                },
            }),
            columnHelper.accessor('createdAt', {
                id: 'createdAt',
                header: 'Lodgement date',
                sortable: 'createdAt',
                cell({ row }) {
                    return formatDate(row.createdAt);
                },
            }),
            columnHelper.accessor((row) => row.PrimaryBrokerContact?.fullName, {
                id: 'broker',
                header: 'Broker',
                cell({ row }) {
                    return (
                        <ContactPopover id={row.PrimaryBrokerContact.id}>
                            {row.PrimaryBrokerContact?.fullName}
                        </ContactPopover>
                    );
                },
            }),
            columnHelper.actions({
                cell({ row }) {
                    return (
                        <Button.OpenInNewTab
                            to="/console/$orgId/claims/details/$id/manage"
                            params={{
                                orgId,
                                id: row.id,
                            }}
                            tooltip="Open in new tab"
                        />
                    );
                },
            }),
        ],
        rowKey(row) {
            return String(row.id);
        },
        queryParams,
        onParamsChange: setQueryParams,
        pagination: {
            position: 'both',
            maxAccessibleRecords: DEFAULT_MAX_ACCESSIBLE_RECORDS,
        },
        columnVisibility: {
            enabled: true,
            persistKey: LIST_TABLE_PERSIST_KEYS.claims,
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => listClaimsQuery.refetch(),
        },
        rowSelection: {
            enabled: true,
            type: 'checkbox',
            preserveSelectedRowKeys: true,
            selectedRowKeys,
            onChange(keys) {
                setSelectedRowKeys(keys);
            },
        },
        showSorterTooltip: false,
        emptyContent:
            listClaimsQuery.isSuccess && listClaimsQuery.data.total === 0 ? (
                <TableEmptyState
                    hasActiveFilters={!isWhereClauseEmpty(queryParams.where)}
                    noMatchesTitle="No claims match your filters"
                    noRecordsTitle="No claims yet"
                    noRecordsDescription="Lodged claims will appear here."
                />
            ) : undefined,
    });

    const builtInViews = useEntityBuiltInViews('owner-ids');
    const views = useTableViews({
        entityType: 'CLAIM',
        config: filterConfig,
        configReady: filterConfigReady,
        urlFilterKey: 'claim-filters',
        builtInViews,
        initialFilters: searchParams['claim-filters'],
        initialActiveViewId: searchParams.view,
        queryParams,
        setQueryParams,
        table: tableProps,
        columnPersistKey: 'claims-table-columns',
        defaultPageSize: DEFAULT_PAGE_SIZE,
        onResolved: () => setViewsReady(true),
    });

    return (
        <PageController route={Route}>
            <TableViewsBar controller={views.controller} />
            <TableToolbar
                filters={
                    <FilterBar
                        config={filterConfig}
                        filters={views.filterState.filters}
                        whereClause={views.filterState.whereClause}
                        onAddFilter={views.filterState.addFilter}
                        onUpdateFilter={views.filterState.updateFilter}
                        onRemoveFilter={views.filterState.removeFilter}
                        onClearFilters={views.filterState.clearFilters}
                    />
                }
                variant="enhanced"
                actions={
                    <Button
                        type="secondary"
                        disabled={!selectedRowKeys.length}
                        onClick={() =>
                            setReassignmentAction([
                                'selected',
                                (selectedRowKeys as (string | number)[]).map((k) => BigInt(String(k))),
                            ])
                        }
                    >
                        Reassign selected claims
                    </Button>
                }
            />

            <ClaimReassignmentDrawer
                action={reassignmentAction}
                onClose={(resetSelectedRowKeys) => {
                    setReassignmentAction(['none']);

                    if (resetSelectedRowKeys) {
                        setSelectedRowKeys([]);
                    }
                }}
            />

            <PrismaTable
                {...tableProps}
                preloadChunk="/console/$orgId/claims/details/$id/manage"
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/claims/details/$id/manage',
                                params: { orgId, id },
                            },
                            e,
                        );
                    },
                })}
            />
        </PageController>
    );
}
